/**
 * `FormFields` removed: used here for six field-name strings (EMAIL,
 * PASSWORD, FIRST_NAME, LAST_NAME, PROVINCE, CITY) and the constant doesn't
 * exist in this project. Replaced with local string constants matching the
 * exact field keys used in useForm's defaultValues / setValue / watch calls
 * below — not arbitrary, just inlined.
 *
 * `CategoryValues` removed/stubbed: this was spread directly into the
 * signup payload (`...CategoryValues`) in two places, presumably to set
 * some default category-interest selection on the new customer record
 * (similar in spirit to SignUp/PreferencesForm's auto-select-all behavior).
 * No category list/defaults exist yet at this point in the flow, so it's
 * stubbed as an empty object for now (spreading {} is a safe no-op) — fix
 * this once a real default category set is decided. Search "STUB:" below.
 *
 * FIX: this screen was calling `signUpActions.signUp` / `setBusiness` /
 * `setSignupProgress` — the *business* signup module's actions, which
 * either don't exist (setBusiness, setSignupProgress) or hit the wrong
 * endpoint (signUp -> /users/business-sign-up/). The customer signup logic
 * has now been ported into the same `signup` module under a `customer`
 * prefix (see coupon-common/src/modules/signup). Switched every call below
 * to the customer-prefixed equivalents.
 *
 * DESIGN: restyled to match BusinessDetailsPage.jsx — swapped the
 * CSSModule/styleName setup for the shared AuthFlow.css class system
 * (auth-page / auth-card / auth-field, etc.) and added the password
 * show/hide toggle used everywhere else in the auth flow. No functional
 * changes: same react-hook-form setup, same Firebase signup logic, same
 * province/city handling.
 *
 * WIDTH: card now uses auth-card-medium (620px cap, defined in
 * AuthFlow.css) instead of the default auth-card 460px cap — matches the
 * "increase the signup section width" request. Still fully responsive:
 * auth-card is width:100% under the hood, auth-card-medium just raises
 * the ceiling, and it collapses to 100% at the tablet breakpoint same as
 * auth-card-wide does.
 *
 * SELECT STYLING: removed the inline style={{...}} on the province
 * <select> now that AuthFlow.css defines `.auth-field select` — same
 * visual result, just driven by the stylesheet like every other field
 * instead of one-off inline styles.
 *
 * FIRST/LAST NAME: already present, wired to FIELD_FIRST_NAME /
 * FIELD_LAST_NAME, rendered as a two-column auth-form-grid row right
 * after Password/Confirm Password. Regardless of `provider`
 * (MANUAL/GOOGLE/APPLE) — only Password/Confirm Password are gated to
 * MANUAL only.
 */
import '../../../../src/style/Authflow.css';

import React, { useEffect, useState } from 'react';

import { createUserWithEmailAndPassword } from 'firebase/auth';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { CityAutocomplete, Spinner } from 'components';
import { actions as locationActions, duck as locationsDuck } from 'coupon-common/src/modules/locations';
import { actions as referralCodesActions } from 'coupon-common/src/modules/referralCodes';
import { actions as signUpActions } from 'coupon-common/src/modules/signup';
import {
  dispatchAsAsync,
  getNormalizedEntity,
} from 'coupon-common/src/utils';
import { extractFirebaseNames } from 'coupon-common/src/utils/extractNames';

import ICON_MAIL from '../../../../src/assets/icons/icon-envelope.svg';
import ICON_KEY from '../../../../src/assets/icons/icon-key.svg';
import ICON_PERSON from '../../../../src/assets/icons/icon-person.svg';
import IMG_LOCATION_BG from '../../../../src/assets/images/location_bg.webp';
// NOTE: paths below mirror the depth of the existing `firebaseAuth` import
// (4 levels up into `src/`). Verify these resolve correctly for wherever
// this file actually lives — adjust if Logo / icons / AuthFlow.css sit
// somewhere else in your tree.
import { firebaseAuth } from '../../../../src/firebase';
import Logo from '../../../../src/Logo/Logo';
import { RouteNames } from '../../../constants';
import messages from './messages';

const provincesKey = locationsDuck.provincesEntity.key;

// Field names used throughout this form (replaces the missing FormFields constant).
const FIELD_EMAIL = 'email';
const FIELD_PASSWORD = 'password';
const FIELD_CONFIRM_PASSWORD = 'confirmPassword';
const FIELD_FIRST_NAME = 'first_name';
const FIELD_LAST_NAME = 'last_name';
const FIELD_PROVINCE = 'province';
const FIELD_CITY = 'city';

// Default category interests for new customer signups — matches mobile's
// hardcoded default set.
const DEFAULT_SIGNUP_CATEGORIES = { categories: [22, 23, 24, 25, 26] };

/* ── inline eye icons, no external asset dependency (matches LoginForm) ── */
function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M1 9s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z"
        stroke="#8A8A8A"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <circle cx="9" cy="9" r="2.4" stroke="#8A8A8A" strokeWidth="1.4" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M1 9s3-6 8-6c1.5 0 2.8.4 3.9 1M17 9s-1.1 2.2-3.1 3.8M9 15c-5 0-8-6-8-6a14.5 14.5 0 013-3.8"
        stroke="#8A8A8A"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M7 9a2 2 0 002.8 1.8M11 9a2 2 0 00-2.7-1.9" stroke="#8A8A8A" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M1 1l16 16" stroke="#8A8A8A" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

/* ── Province pill selector — same visual pattern as BusinessCategoriesField's
   CategoryPillField / ServiceableAreaSection's city picker
   (auth-serviceable-box / auth-city-pill-row / auth-city-pill), just
   single-select and wired to react-hook-form's Controller field object
   instead of a react-final-form { input, meta } pair. ── */
function ProvincePillField({ value, onChange, options = [], isLoading, loadingError, disabled }) {
  return (
    <div className="auth-serviceable-box">
      {isLoading && <p className="text-muted">Loading…</p>}
      {loadingError && (
        <span className="auth-field-error">Something went wrong loading provinces.</span>
      )}

      {!isLoading && !loadingError && (
        <div className="auth-city-pill-row">
          {options.map(o => (
            <button
              key={o.id}
              type="button"
              className={`auth-city-pill${String(value) === String(o.id) ? ' is-selected' : ''}`}
              onClick={() => onChange(o.id)}
              disabled={disabled}
            >
              {o.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const EmailPasswordForm = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Carried over from SignIn (Google/Apple SSO) via router state, or this is
  // a fresh manual signup with nothing pre-filled.
  const { userInfo, code, provider = 'MANUAL', business } = location.state || {};

  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const locations = useSelector(state => state.locations);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      [FIELD_EMAIL]: userInfo?.email || '',
      [FIELD_FIRST_NAME]: userInfo?.first_name || '',
      [FIELD_LAST_NAME]: userInfo?.last_name || '',
    },
  });

  const provinceValue = watch(FIELD_PROVINCE);

  useEffect(() => {
    dispatch(locationActions.getProvinces());
    dispatch(referralCodesActions.setCustomerCode(code));
  }, [dispatch, code]);

  useEffect(() => {
    if (provinceValue) {
      setValue(FIELD_CITY, null);
    }
  }, [provinceValue, setValue]);

  const {
    entityList: provinceOptions = [],
    error: provinceLoadingError,
    isFetching: isProvinceFetching,
  } = getNormalizedEntity(locations, provincesKey, intl, messages.entityProvinces);

  const goToSuccessfulSignUp = signupBusiness => {
    if (signupBusiness && Object.keys(signupBusiness).length) {
      navigate(RouteNames.SUCCESSFUL_SIGN_UP, { state: { business: signupBusiness } });
    } else {
      dispatch(signUpActions.customerSetSignupProgress(false));
      navigate(RouteNames.SUCCESSFUL_SIGN_UP);
    }
  };

  const handleSignup = async formValues => {
    const { email, password, first_name, last_name, city } = formValues;
    setError(null);

    try {
      if (provider === 'MANUAL') {
        const userCredential = await createUserWithEmailAndPassword(firebaseAuth, email, password);
        const firebaseIdToken = await userCredential.user.getIdToken(true);

        if (firebaseIdToken) {
          const { user } = userCredential;
          const { first_name: fbFirst, last_name: fbLast } = await extractFirebaseNames(user.displayName);

          const userInfoResults = {
            email: user.email,
            firebase_uid: user.uid,
            firebaseIdToken,
            first_name: first_name || fbFirst,
            last_name: last_name || fbLast,
            city,
            code,
            coupon_search_range: { id: 50000, name: '50 km' },
            ...DEFAULT_SIGNUP_CATEGORIES,
          };

          dispatch(signUpActions.customerSetBusiness(business));

          const submitErrors = await dispatchAsAsync(signUpActions.customerSignUp(userInfoResults), dispatch);

          if (!submitErrors) {
            goToSuccessfulSignUp(business);
          } else {
            console.error('Signup errors:', submitErrors);
          }
        }
      } else if (provider === 'GOOGLE' || provider === 'APPLE') {
        const firebaseUserInfo = {
          ...userInfo,
          first_name,
          last_name,
          city,
          code,
          coupon_search_range: { id: 50000, name: '50 km' },
          ...DEFAULT_SIGNUP_CATEGORIES,
        };

        dispatch(signUpActions.customerSetBusiness(business));

        const submitErrors = await dispatchAsAsync(signUpActions.customerSignUp(firebaseUserInfo), dispatch);

        if (!submitErrors) {
          goToSuccessfulSignUp(business);
        } else {
          console.error('Signup errors:', submitErrors);
        }
      }
    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please try again later.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
  };

  return (
    <div className="auth-page auth-simple-page">
      <img src={IMG_LOCATION_BG} alt="" className="auth-simple-bg" />
      <header className="auth-navbar auth-navbar-simple">
        <button type="button" className="auth-logo-btn" onClick={() => navigate('/')}>
          <Logo />
        </button>
      </header>

      <div className="auth-card-wrap auth-card-wrap-loose">
        <div className="auth-card auth-card-medium">
          <h2 className="auth-form-title">{intl.formatMessage(messages.welcomeScreen)}</h2>

          <form onSubmit={handleSubmit(handleSignup)}>
            <div className="auth-form-grid">
              <Controller
                control={control}
                name={FIELD_FIRST_NAME}
                render={({ field }) => (
                  <label className="auth-field auth-field-plain">
                    <img src={ICON_PERSON} alt="" className="auth-field-icon" />
                    <div>
                      <div className="auth-field-label">{intl.formatMessage(messages.firstName)}</div>
                      <input id="signup-first-name" type="text" {...field} />
                    </div>
                    {errors[FIELD_FIRST_NAME] && (
                      <span className="auth-field-error">{errors[FIELD_FIRST_NAME].message}</span>
                    )}
                  </label>
                )}
              />
              <Controller
                control={control}
                name={FIELD_LAST_NAME}
                render={({ field }) => (
                  <label className="auth-field auth-field-plain">
                    <img src={ICON_PERSON} alt="" className="auth-field-icon" />
                    <div>
                      <div className="auth-field-label">{intl.formatMessage(messages.lastName)}</div>
                      <input id="signup-last-name" type="text" {...field} />
                    </div>
                    {errors[FIELD_LAST_NAME] && (
                      <span className="auth-field-error">{errors[FIELD_LAST_NAME].message}</span>
                    )}
                  </label>
                )}
              />
            </div>

            <Controller
              control={control}
              name={FIELD_EMAIL}
              render={({ field }) => (
                <label className="auth-field auth-field-full">
                  <img src={ICON_MAIL} alt="" className="auth-field-icon" />
                  <div>
                    <div className="auth-field-label">{intl.formatMessage(messages.email)}</div>
                    <input
                      id="signup-email"
                      type="email"
                      autoComplete="email"
                      disabled={provider !== 'MANUAL'}
                      {...field}
                    />
                  </div>
                  {errors[FIELD_EMAIL] && (
                    <span className="auth-field-error">{errors[FIELD_EMAIL].message}</span>
                  )}
                </label>
              )}
            />

            {provider === 'MANUAL' && (
              <div className="auth-form-grid">
                <Controller
                  control={control}
                  name={FIELD_PASSWORD}
                  render={({ field }) => (
                    <label className="auth-field auth-field-plain">
                      <img src={ICON_KEY} alt="" className="auth-field-icon" />
                      <div>
                        <div className="auth-field-label">{intl.formatMessage(messages.password)}</div>
                        <input
                          id="signup-password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          {...field}
                        />
                      </div>
                      <button
                        type="button"
                        className="auth-field-toggle"
                        onClick={() => setShowPassword(s => !s)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                      {errors[FIELD_PASSWORD] && (
                        <span className="auth-field-error">{errors[FIELD_PASSWORD].message}</span>
                      )}
                    </label>
                  )}
                />

                <Controller
                  control={control}
                  name={FIELD_CONFIRM_PASSWORD}
                  rules={{
                    required: 'Please confirm your password',
                    validate: (value, formValues) =>
                      value === formValues[FIELD_PASSWORD] || 'Passwords do not match',
                  }}
                  render={({ field }) => (
                    <label className="auth-field auth-field-plain">
                      <img src={ICON_KEY} alt="" className="auth-field-icon" />
                      <div>
                        <div className="auth-field-label">Confirm Password</div>
                        <input
                          id="signup-confirm-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          {...field}
                        />
                      </div>
                      <button
                        type="button"
                        className="auth-field-toggle"
                        onClick={() => setShowConfirmPassword(s => !s)}
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                      </button>
                      {errors[FIELD_CONFIRM_PASSWORD] && (
                        <span className="auth-field-error">{errors[FIELD_CONFIRM_PASSWORD].message}</span>
                      )}
                    </label>
                  )}
                />
              </div>
            )}

            <Controller
              control={control}
              name={FIELD_PROVINCE}
              rules={{ required: 'Province is required' }}
              render={({ field: { onChange, value } }) => (
                <div className="auth-form-section">
                  <div className="auth-field-label">{intl.formatMessage(messages.province)}</div>
                  <ProvincePillField
                    value={value}
                    onChange={onChange}
                    options={provinceOptions}
                    isLoading={isProvinceFetching}
                    loadingError={provinceLoadingError}
                  />
                  {errors[FIELD_PROVINCE] && (
                    <span className="auth-field-error">{errors[FIELD_PROVINCE].message}</span>
                  )}
                </div>
              )}
            />
            {provinceLoadingError && <p className="auth-field-error">{provinceLoadingError}</p>}

            {provinceValue && (
              <Controller
                control={control}
                name={FIELD_CITY}
                rules={{ required: 'City is required' }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <div className="auth-form-section">
                    <div className="auth-field-label">{intl.formatMessage(messages.city)}</div>
                    <CityAutocomplete
                      inputId="signup-city"
                      province={provinceValue}
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      error={errors[FIELD_CITY]?.message}
                    />
                  </div>
                )}
              />
            )}

            {error && <div className="auth-error">{error}</div>}

            <button type="submit" className="auth-btn auth-btn-solid auth-btn-block" disabled={isSubmitting}>
              {isSubmitting ? <Spinner show size="sm" /> : intl.formatMessage(messages.createBtn)}
            </button>

            <p className="auth-footer-text">
              By clicking Create Account, you agree to have read our{' '}
              <button type="button" className="auth-link" onClick={() => navigate(RouteNames.PRIVACY_POLICY)}>
                {intl.formatMessage(messages.privacyPolicyText)}
              </button>
              .
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailPasswordForm;